import React, { useState, useEffect } from 'react';
import { Sun, Cloud, Wind, Droplets, Waves, Compass, AlertTriangle, RefreshCw, Calendar, Clock } from 'lucide-react';



const CottesloeBeachApp = () => {
  const [weatherData, setWeatherData] = useState({
    date: "",
    forecasts: [],
    lastUpdated: "",
    dataAge: 0,
    bomSource: ""
  });

  
  const [currentForecast, setCurrentForecast] = useState({
    time: "",
    waveHeight: 0,
    windSpeed: 0,
    windDirection: "",
    temperature: 0,
  });
  
  const [isIdeal, setIsIdeal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  
  const [userSettings, setUserSettings] = useState({
    maxWindSpeed: 20,
    maxWaveHeight: 1,
    minTemperature: 28
  });

  const [activeTab, setActiveTab] = useState('tomorrow'); // Default to tomorrow
  const [todayData, setTodayData] = useState({
  date: "",
  forecasts: [],
  lastUpdated: "",
  dataAge: 0
  });
  const [tomorrowData, setTomorrowData] = useState({
  date: "",
  forecasts: [],
  lastUpdated: "",
  dataAge: 0
  });

  // Function to calculate data age in minutes
  const calculateDataAge = (lastUpdatedTime) => {
    const lastUpdated = new Date(lastUpdatedTime);
    const now = new Date();
    return Math.round((now - lastUpdated) / (1000 * 60)); // Convert ms to minutes
  };

  // Function to get data freshness status
  const getDataFreshnessStatus = (dataAge) => {
    if (dataAge <= 60) return { status: 'fresh', color: 'bg-green-100 text-green-800', message: 'Data is fresh' };
    if (dataAge <= 360) return { status: 'moderate', color: 'bg-yellow-100 text-yellow-800', message: 'Data is getting old' };
    return { status: 'stale', color: 'bg-red-100 text-red-800', message: 'Data is stale - please update' };
  };

  // Function to format relative time
  const formatRelativeTime = (minutes) => {
    if (minutes < 60) return `${minutes} minutes ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    const days = Math.floor(hours / 24);
    return `${days} day${days > 1 ? 's' : ''} ago`;
  };

  const handleTabChange = (tab) => {
  setActiveTab(tab);
  const currentData = tab === 'today' ? todayData : tomorrowData;
  
  if (currentData.forecasts && currentData.forecasts.length > 0) {
      setWeatherData(currentData);
      
      // FIX: Apply the same logic as the initial fetch to correctly set the forecast
      let forecastIndex = 0;
      if (tab === 'tomorrow') {
        // Look for 8:00 AM for tomorrow
        const eightAmIndex = currentData.forecasts.findIndex(f => f.time === '8:00 AM');
        forecastIndex = eightAmIndex >= 0 ? eightAmIndex : 0;
      } else {
        // For today, use first available
        const validIndex = currentData.forecasts.findIndex(f => f.temperature !== null);
        forecastIndex = validIndex >= 0 ? validIndex : 0;
      }
      setCurrentForecast(currentData.forecasts[forecastIndex]);

    } else {
    // Fetch fresh data if none exists
    const fetchFreshData = async () => {
      try {
        const apiResponse = await fetch('/api/update-weather');
        if (apiResponse.ok) {
          const apiResult = await apiResponse.json();
          if (apiResult.data) {
            const todayAge = calculateDataAge(apiResult.data.today.lastUpdated);
            const tomorrowAge = calculateDataAge(apiResult.data.tomorrow.lastUpdated);
            
            setTodayData({
              ...apiResult.data.today,
              dataAge: todayAge,
              needsRefresh: todayAge > 60
            });
            
            setTomorrowData({
              ...apiResult.data.tomorrow,
              dataAge: tomorrowAge,
              needsRefresh: tomorrowAge > 60
            });
            
            // Now set the requested tab data
            const requestedData = tab === 'today' ? apiResult.data.today : apiResult.data.tomorrow;
            setWeatherData(requestedData);

            let forecastIndex = 0;
            if (tab === 'tomorrow') {
              // Look for 8:00 AM for tomorrow
              const eightAmIndex = requestedData.forecasts.findIndex(f => f.time === '8:00 AM');
              forecastIndex = eightAmIndex >= 0 ? eightAmIndex : 0;
            } else {
              // For today, use first available
              const validIndex = requestedData.forecasts.findIndex(f => f.temperature !== null);
              forecastIndex = validIndex >= 0 ? validIndex : 0;
            }

            setCurrentForecast(requestedData.forecasts[forecastIndex]);
          }
        }
      } catch (error) {
        console.error('Error fetching fresh data for tab change:', error);
      }
    };
    
    fetchFreshData();
  }
};

// TEMPORARY DEBUG - remove after testing
// console.log('Debug info:', {
//  activeTab,
//  todayDataHasForecasts: todayData.forecasts?.length > 0,
//  tomorrowDataHasForecasts: tomorrowData.forecasts?.length > 0,
//  currentWeatherDataHasForecasts: weatherData.forecasts?.length > 0
//});

  // Function to actually refresh data via API
const refreshData = async () => {
  setRefreshing(true);
  setError("Refreshing data from Bureau of Meteorology...");
  
  try {
    const response = await fetch('/api/update-weather');
    if (response.ok) {
      const result = await response.json();
      if (result.data) {
        // Use the fresh data directly from API response
        const freshData = {
          ...result.data,
          dataAge: 0,
          needsRefresh: false
        };
        
        setWeatherData(freshData);
        setCurrentForecast(freshData.forecasts[2]); // Morning forecast (8am)
        setError("Data refreshed successfully!");
      } else {
        setError("API returned no data. Please try again.");
      }
    } else {
      setError("Failed to refresh data. Please try again.");
    }
  } catch (error) {
    setError("Network error. Please check your connection.");
  }
  
  setRefreshing(false);
};

// Effect to fetch data
useEffect(() => {
  const fetchWeatherData = async () => {
  try {
    setIsLoading(true);
    
    // Fetch fresh data from API
    const apiResponse = await fetch('/api/update-weather');
    if (apiResponse.ok) {
      const apiResult = await apiResponse.json();
      if (apiResult.data) {
        // Handle API structure with today and tomorrow
        const todayAge = calculateDataAge(apiResult.data.today.lastUpdated);
        const tomorrowAge = calculateDataAge(apiResult.data.tomorrow.lastUpdated);
        
        setTodayData({
          ...apiResult.data.today,
          dataAge: todayAge,
          needsRefresh: todayAge > 60
        });
        
        setTomorrowData({
          ...apiResult.data.tomorrow,
          dataAge: tomorrowAge,
          needsRefresh: tomorrowAge > 60
        });
        
        // Set current display based on active tab
        const currentData = activeTab === 'today' ? apiResult.data.today : apiResult.data.tomorrow;
        setWeatherData({
          ...currentData,
          dataAge: activeTab === 'today' ? todayAge : tomorrowAge,
          needsRefresh: (activeTab === 'today' ? todayAge : tomorrowAge) > 60
        });

        // Find forecast index - prefer 8AM for tomorrow, first available for today
        let forecastIndex = 0;
        if (activeTab === 'tomorrow') {
          // Look for 8:00 AM specifically for tomorrow
          const eightAmIndex = currentData.forecasts.findIndex(f => f.time === '8:00 AM');
          forecastIndex = eightAmIndex >= 0 ? eightAmIndex : 0;
        } else {
          // For today, just use first available
          const validIndex = currentData.forecasts.findIndex(f => f.temperature !== null);
          forecastIndex = validIndex >= 0 ? validIndex : 0;
        }

        setCurrentForecast(currentData.forecasts[forecastIndex]);

        setIsLoading(false);
        setError(null);
        return;
      }
    }
    
    throw new Error('API returned no data');
    
  } catch (err) {
    console.error("Error fetching weather data:", err);
    setError(`Failed to load weather data: ${err.message}`);
    setIsLoading(false);
  }
};
  
  // Initial fetch
  fetchWeatherData();

  // Check if we need fresh data and refresh accordingly
  const checkAndRefreshData = async () => {
    await fetchWeatherData(); // Get current data
    if (weatherData.dataAge > 480) { // If older than 8 hours
      fetch('/api/update-weather'); // Trigger fresh BOM fetch
    }
  };

  // Refresh data every 30 minutes
  const intervalId = setInterval(checkAndRefreshData, 30 * 60 * 1000);

  return () => clearInterval(intervalId);
}, []);

// Effect to check if conditions are ideal
useEffect(() => {
  if (currentForecast) {
    // Check if wind direction is easterly (E, ENE, ESE)
    const isEasterly = ['E', 'ENE', 'ESE'].includes(currentForecast.windDirection);
    
    setIsIdeal(
      currentForecast.windSpeed <= userSettings.maxWindSpeed &&
      currentForecast.waveHeight <= userSettings.maxWaveHeight &&
      currentForecast.temperature >= userSettings.minTemperature &&
      isEasterly // Prefer easterly winds for ideal conditions
    );
  }
}, [currentForecast, userSettings]);

  const handleSettingsChange = (e) => {
    const { name, value } = e.target;
    setUserSettings({
      ...userSettings,
      [name]: parseFloat(value)
    });
  };

  /// const getRecommendation = () => {
//   if (isIdeal) {
//     return "Perfect conditions predicted for a swim at Cottesloe Beach! The water should be glassy and calm.";
//   } else {
//     const reasons = [];
//     if (currentForecast.windSpeed > userSettings.maxWindSpeed) reasons.push("windy");
//     if (currentForecast.waveHeight > userSettings.maxWaveHeight) reasons.push("choppy water");
//     if (currentForecast.temperature < userSettings.minTemperature) reasons.push("cool temperature");
//     if (!['E', 'ENE', 'ESE'].includes(currentForecast.windDirection)) reasons.push("non-easterly wind direction");
//     
//     return `Conditions are not ideal for swimming due to ${reasons.join(', ')}.`;
//   }
// };

  // Weather icon based on conditions
  const getWeatherIcon = () => {
    // In a real app, we'd determine this based on weather conditions
    // For now, we'll just use temperature
    if (currentForecast.temperature >= 28) {
      return <Sun size={48} className="text-yellow-500" />;
    } else if (currentForecast.temperature >= 22) {
      return <Sun size={48} className="text-yellow-400" />;
    } else {
      return <Cloud size={48} className="text-gray-400" />;
    }
  };
  
  // Get wind direction arrow rotation
  const getWindDirectionRotation = (direction) => {
    const directions = {
      'N': 0, 'NNE': 22.5, 'NE': 45, 'ENE': 67.5,
      'E': 90, 'ESE': 112.5, 'SE': 135, 'SSE': 157.5,
      'S': 180, 'SSW': 202.5, 'SW': 225, 'WSW': 247.5,
      'W': 270, 'WNW': 292.5, 'NW': 315, 'NNW': 337.5
    };
    
    return directions[direction] || 0;
  };

  // Status color
  const statusColor = isIdeal ? 'bg-green-500' : 'bg-amber-500';
  const freshnessStatus = getDataFreshnessStatus(weatherData.dataAge);

  if (isLoading && !weatherData.forecasts.length) {
    return <div className="flex justify-center items-center h-screen">Loading Cottesloe Beach weather data...</div>;
  }
  
  if (error) {
    return <div className="p-4 bg-red-100 text-red-700 rounded-lg">{error}</div>;
  }

  return (
    <div className="max-w-md mx-auto p-4 bg-blue-50 rounded-lg shadow-lg">
      <h1 className="text-2xl font-bold text-center mb-2">Cottesloe Beach Weather</h1>
      
      
      {/* Date and Tab Navigation */}
<div className="mb-4">
  <div className="text-center mb-2">
    {/* Tab buttons */}
    <div className="flex justify-center gap-4 mb-3">
      <button 
        onClick={() => handleTabChange('tomorrow')}
        className={`px-4 py-2 rounded-lg font-medium transition-colors ${
          activeTab === 'tomorrow' 
            ? 'bg-blue-600 text-white' 
            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
        }`}
      >
        Tomorrow's Forecast
      </button>
      <button 
        onClick={() => handleTabChange('today')}
        className={`px-4 py-2 rounded-lg font-medium transition-colors ${
          activeTab === 'today' 
            ? 'bg-blue-600 text-white' 
            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
        }`}
      >
        Today's Forecast
      </button>
    </div>
    
    {/* Current date display */}
    <div className="flex items-center justify-center gap-2">
      <Calendar size={18} className="text-blue-600" />
      <span className="text-lg font-semibold text-gray-800">{weatherData.date}</span>
    </div>
  </div>
</div>
            
      {weatherData.needsRefresh && (
        <div className="bg-amber-100 p-2 mb-4 rounded-lg flex items-center text-sm">
          <AlertTriangle className="mr-2 text-amber-500" size={18} />
          <span>Data is {weatherData.dataAge} minutes old. Please run the update script to get fresh data.</span>
          <button 
            onClick={refreshData} 
            className="ml-2 px-2 py-1 bg-amber-500 text-white rounded-md flex items-center hover:bg-amber-600 transition-colors"
            disabled={refreshing}
          >
            {refreshing ? 'Instructions...' : 'How to Update'}
            {!refreshing && <RefreshCw className="ml-1" size={14} />}
          </button>
        </div>
      )}
      
      {/*} <div className={`p-4 mb-4 rounded-lg ${statusColor}`}>
      //   <p className="text-white font-semibold">{getRecommendation()}</p>
      // </div> */}
      
      <div className="bg-white p-4 rounded-lg shadow mb-4">
        <div className="text-center mb-4">
          {getWeatherIcon()}
          <div className="text-lg font-semibold mt-2">Conditions</div>
          <div className="text-xl font-bold">{currentForecast.time}</div>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div className="flex items-center">
            <Wind className="mr-2" size={24} />
            <div>
              <div className="text-sm text-gray-600">Wind</div>
              <div className="font-semibold">{currentForecast.windSpeed} km/h</div>
            </div>
          </div>
          <div className="flex items-center">
            <div className="mr-2 relative">
              <Compass size={24} />
              <div 
                className="absolute inset-0 flex items-center justify-center"
                style={{ transform: `rotate(${getWindDirectionRotation(currentForecast.windDirection)}deg)` }}
              >
                <div className="w-1 h-3 bg-blue-600 rounded-full transform -translate-y-1"></div>
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-600">Direction</div>
              <div className="font-semibold">{currentForecast.windDirection}</div>
            </div>
          </div>
          <div className="flex items-center">
            <Waves className="mr-2" size={24} />
            <div>
              <div className="text-sm text-gray-600">Waves</div>
              <div className="font-semibold">{currentForecast.waveHeight} m</div>
            </div>
          </div>
          <div className="flex items-center">
            <Droplets className="mr-2" size={24} />
            <div>
              <div className="text-sm text-gray-600">Temperature</div>
              <div className="font-semibold">{currentForecast.temperature}°C</div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="bg-white p-4 rounded-lg shadow mb-4">
        <div className="text-center mb-2 text-lg font-semibold">Full Day Forecast</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr>
                <th className="text-left">Time</th>
                <th className="text-center">Temp</th>
                <th className="text-center">Wind</th>
                <th className="text-center">Direction</th>
                <th className="text-center">Waves</th>
                <th className="text-center">Ideal?</th>
              </tr>
            </thead>
            <tbody>
              {weatherData.forecasts.map((forecast, index) => {
                const isEasterly = ['E', 'ENE', 'ESE'].includes(forecast.windDirection);
                const isIdealTime = 
                  forecast.windSpeed <= userSettings.maxWindSpeed &&
                  forecast.waveHeight <= userSettings.maxWaveHeight &&
                  forecast.temperature >= userSettings.minTemperature &&
                  isEasterly;
                  
                return (
                  <tr key={index} className={forecast.time === currentForecast.time ? "bg-blue-50" : ""}>
                    <td className="py-2">{forecast.time}</td>
                    <td className="text-center">{forecast.temperature}°C</td>
                    <td className="text-center">{forecast.windSpeed} km/h</td>
                    <td className="text-center">{forecast.windDirection}</td>
                    <td className="text-center">{forecast.waveHeight} m</td>
                    <td className="text-center">
                      {isIdealTime ? 
                        <span className="text-green-500">✓</span> : 
                        <span className="text-red-500">✗</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      
      <div className="bg-white p-4 rounded-lg shadow">
        <div className="text-center mb-2 text-lg font-semibold">Your Preferences</div>
        <div className="space-y-2">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Max Wind Speed: {userSettings.maxWindSpeed} km/h
            </label>
            <input
              type="range"
              name="maxWindSpeed"
              min="0"
              max="30"
              value={userSettings.maxWindSpeed}
              onChange={handleSettingsChange}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Max Wave Height: {userSettings.maxWaveHeight} m
            </label>
            <input
              type="range"
              name="maxWaveHeight"
              min="0"
              max="2"
              step="0.1"
              value={userSettings.maxWaveHeight}
              onChange={handleSettingsChange}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Min Temperature: {userSettings.minTemperature}°C
            </label>
            <input
              type="range"
              name="minTemperature"
              min="15"
              max="50"
              value={userSettings.minTemperature}
              onChange={handleSettingsChange}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
          </div>
        </div>
      </div>
      
      <div className="mt-4 p-3 bg-blue-100 rounded-lg text-sm text-gray-700">
        <p className="font-medium">Cottesloe Beach Tip:</p>
        <p>Generally ideal conditions are with a light easterly wind under 20km/h and wave height below 1m, which creates glassy, calm water.</p>
      </div>

      <div className="mt-4 text-center text-sm text-gray-600">
  <p>Data sourced from Bureau of Meteorology</p>
  <div className={`text-xs mt-1 ${freshnessStatus.color} px-2 py-1 rounded inline-block`}>
    Data is {freshnessStatus.status} • Last updated {new Date(weatherData.lastUpdated).toLocaleTimeString('en-AU')}
  </div>
</div>
    </div>
  );
};

export default CottesloeBeachApp;